import React, { useState } from 'react';
import { Notification } from '@shootsta/common-react';
import { parseTopupPromo, useTopupCreditsModal } from '@core/shared';

type Props = {
  promo: unknown;
  organisationId?: string;
};

const dismissKey = (organisationId: string) =>
  `topupPromoDismissed:${organisationId}`;

// A dismissal is remembered per workspace against the banner text, so a new
// promo (new text in LaunchDarkly) shows again for everyone.
const readDismissedText = (organisationId?: string) => {
  if (!organisationId) return null;
  try {
    return localStorage.getItem(dismissKey(organisationId));
  } catch {
    return null;
  }
};

/**
 * SOW 49: the top-up promo banner, driven by the `topup-credits-promos` flag.
 * Its "Buy credits" button opens the shell-level Top up Credits modal.
 */
export default function PromoBanner({
  promo: rawPromo,
  organisationId
}: Props) {
  const promo = parseTopupPromo(rawPromo);
  const { open } = useTopupCreditsModal();
  // Hides the banner straight away on dismiss, and still does when storage
  // is blocked. The stored dismissal is read each render (one localStorage
  // read), so switching workspace picks up that workspace's own dismissal.
  const [hiddenThisSession, setHiddenThisSession] = useState<string | null>(
    null
  );
  const dismissedText = readDismissedText(organisationId);

  if (
    !promo ||
    !promo.bannerText ||
    dismissedText === promo.bannerText ||
    hiddenThisSession === promo.bannerText
  ) {
    return null;
  }

  const onDismiss = () => {
    if (organisationId) {
      try {
        localStorage.setItem(dismissKey(organisationId), promo.bannerText);
      } catch {
        // Storage blocked: the banner comes back on the next reload.
      }
    }
    setHiddenThisSession(promo.bannerText);
  };

  return (
    <div style={{ width: '100%' }}>
      <Notification
        fullWidthStyle
        backgroundColour="orange"
        text={promo.bannerText}
        callToActionText="Buy credits"
        onClickCallToAction={open}
        onClickCloseBtn={onDismiss}
      />
    </div>
  );
}
